import { useMsal } from '@azure/msal-react';
import {
  Button,
  Avatar,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Text,
  Tooltip,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  SignOut24Regular,
  Person24Regular,
  WeatherMoon20Regular,
  WeatherSunny20Regular,
} from '@fluentui/react-icons';
import { useThemeMode } from '../../theme/ThemeContext';
import { useCurrentSystemUser } from '../../hooks/useSystemUsers';
import { StaffRoleBadges } from '../common/StaffBadge';
import { LiveUpdatesBadge } from '../common/LiveUpdatesBadge';
import MomentsToggle from '../moments/MomentsToggle';
import LanguageToggle from './LanguageToggle';
import { FeedbackMenu } from '../feedback/FeedbackMenu';
import { NotificationBell } from '../../shell/NotificationBell';
import { useTranslation } from '../../i18n';

const useStyles = makeStyles({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 24px',
    height: '48px',
    backgroundColor: 'var(--sup-header-bg)',
    color: 'var(--sup-header-fg)',
    borderBottom: '1px solid var(--sup-header-border)',
    boxShadow: tokens.shadow4,
  },
  title: {
    color: 'var(--sup-header-title)',
  },
  // Light mode: white glyphs on the brand-blue bar, translucent-white hover
  // (Fluent's default subtle hover is a light gray that would swallow white
  // text on the blue). Dark mode: neutral glyphs on the deep surface, with
  // the theme's own subtle hover.
  headerButtonLight: {
    color: '#ffffff',
    ':hover': {
      color: '#ffffff',
      backgroundColor: 'rgba(255, 255, 255, 0.16)',
    },
    ':hover:active': {
      color: '#ffffff',
      backgroundColor: 'rgba(255, 255, 255, 0.24)',
    },
  },
  headerButtonDark: {
    color: tokens.colorNeutralForeground2,
    ':hover': {
      color: tokens.colorNeutralForeground1,
    },
  },
});

export default function AppHeader() {
  const { instance, accounts } = useMsal();
  const account = accounts[0];
  const { data: currentUser } = useCurrentSystemUser();
  const { mode, toggleMode } = useThemeMode();
  const { t } = useTranslation();
  const styles = useStyles();
  const headerButtonClass =
    mode === 'dark' ? styles.headerButtonDark : styles.headerButtonLight;

  const handleLogout = () => {
    instance.logoutRedirect();
  };

  const themeToggleLabel = mode === 'dark' ? t('header.theme.toLight') : t('header.theme.toDark');

  return (
    <header className={styles.header}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <Text weight="semibold" size={500} className={styles.title}>
          {t('shell.appName')}
        </Text>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        {/* Renders nothing while the realtime channel is healthy — see LiveUpdatesBadge. */}
        <LiveUpdatesBadge />
        <StaffRoleBadges
          staffIdentifier={currentUser?.systemuserid ?? account?.name}
          size="table"
        />
        <MomentsToggle className={headerButtonClass} />
        <LanguageToggle className={headerButtonClass} />
        <Tooltip content={themeToggleLabel} relationship="label">
          <Button
            appearance="subtle"
            className={headerButtonClass}
            icon={mode === 'dark' ? <WeatherSunny20Regular /> : <WeatherMoon20Regular />}
            onClick={toggleMode}
          />
        </Tooltip>
        {/* Staff notifications (fleet spec v0.2 §8): the platform's feed, every app's items, read in core. */}
        <NotificationBell />
        {/* Report a problem / Suggest an improvement (fleet feedback contract v1): on every staff page. */}
        <FeedbackMenu className={headerButtonClass} />

        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <Button
              appearance="subtle"
              className={headerButtonClass}
              icon={
                <Avatar
                  name={account?.name || t('header.userFallback')}
                  size={28}
                  color="colorful"
                />
              }
            >
              {account?.name || t('header.userFallback')}
            </Button>
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <MenuItem icon={<Person24Regular />}>
                {account?.username}
              </MenuItem>
              <MenuItem icon={<SignOut24Regular />} onClick={handleLogout}>
                {t('header.signOut')}
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
      </div>
    </header>
  );
}
